'use client';

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { usePathname, useRouter } from 'next/navigation';

import { BioUserAccessProvider } from '@ps/features/bio/contexts/user-access/provider';
import { useBioUserAccess } from '@ps/features/bio/contexts/user-access/useBioUserAccess';
import { SubscribeDialog } from '@ps/features/subscribe/components/SubscribeDialog';
import { useCreatorProfile } from '@ps/features/subscribe/hooks/useCreatorProfile';
import {
  useAudioMuteContext,
  useVideoClipsContext,
} from '@ps/features/video-player/contexts/video-player';
import type { AnyVideo } from '@ps/features/video-player/functions';
import { profileSlimmed } from '@ps/functions/bio/profileSlim';
import { useBehaviorSubject } from '@ps/functions/hooks/useBehaviorSubject';
import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreExtendedUserAccessLevel,
} from '@ps/models/lib/creator-subscription';
import {
  useCurrentUserId,
  useIsGuestUser,
  useLogin,
} from '@ps/ui/contexts/auth';
import { X } from 'lucide-react';

import { SubscriptionLevelBadge } from '../../../subscription/components/SubscriptionLevelBadge';
import { subscriptionsToList } from '../../../subscription/configuration/functions/access-control';
import { getSubscriptionDisplayName } from '../../../subscription/configuration/functions/subscription-display';
import { useFetchAllSubscriptions } from '../../../subscription/configuration/hooks/useFetchAllSubscriptions';
import { CoursePurchaseProvider } from '../../visitor/contexts/course-purchase/provider';
import { useCoursePurchased } from '../contexts/course-purchase/context';
import {
  clipRequiredAccessLevel,
  isAccessGated,
  isClipLockedForViewer,
} from '../functions/seriesClipAccess';

/**
 * The standalone-purchase option a locked COURSE clip's paywall offers — 1:1
 * with RN's "Unlock this Course for $X" button in `AccessBlockedBySubscriptionView`.
 * Supplied by the course clip pages (which own the price + checkout URL); omitted
 * for series / non-purchasable-course players so the paywall stays subscribe-only.
 */
export interface CourseClipUnlock {
  /** Discount-aware price to charge, pre-formatted (e.g. `'$5.50'`). */
  priceLabel: string;
  /** Pre-discount price to strike through, or `undefined` when not discounted. */
  originalPriceLabel?: string;
  /** Opens the course checkout (soft-nav to the checkout overlay). */
  onPurchase: () => void;
}

/**
 * Clip subscription/follow paywall — the Next mirror of PopshopRN's
 * `AccessBlockedBySubscriptionView` flow (`shared-video/content.tsx` →
 * `lockedVideoTapped`): a locked clip plays a short preview, then a blurred
 * overlay prompts the viewer to follow (free FOLLOWER tier) or subscribe (paid
 * tier) before it unlocks.
 *
 * Shared across every clip-play surface — the CollectionButton dialog
 * (`SeriesVideoPlayer`), the standalone `.../videos/[video_id]` page
 * (`ClipVideoPage`), and the intercepted swipe deck (`SeriesClipsPlayer`) — so
 * the gate lives in ONE place instead of three copies (originally a dormant
 * commented-out block in `series-video-player.tsx`).
 *
 * ## Host wiring
 * Wrap the video column in `<ClipAccessBoundary sellerId=…>`, render
 * `<ClipAccessMonitor videos=… />` INSIDE the host's `VideoClipsProvider` (it
 * reads `currentIndex$` / `muted$`), and render `<ClipAccessOverlay />` as a
 * sibling of the provider inside the same positioned video-column div:
 *
 * ```tsx
 * <ClipAccessBoundary sellerId={sellerId}>
 *   <div className="relative flex h-full flex-col …"> // positioned parent
 *     <VideoClipsProvider …>
 *       <ClipAccessMonitor videos={videos} />
 *       <VideoClips … />
 *     </VideoClipsProvider>
 *     <ClipAccessOverlay />
 *   </div>
 * </ClipAccessBoundary>
 * ```
 *
 * `ClipAccessBoundary` mounts its own `BioUserAccessProvider(creatorId=sellerId)`
 * so the gate resolves the viewer's real standing on EVERY surface — the
 * standalone clip page and course layout don't otherwise mount that provider, so
 * without this a real subscriber would be wrongly gated.
 */

// ─── Shared gate state (monitor writes, overlay reads) ──────────────────────

interface ClipAccessState {
  sellerId: string;
  /** The clip currently blocking playback, or `null` when nothing is locked. */
  lockedVideo: AnyVideo | null;
  setLockedVideo: (video: AnyVideo | null) => void;
  /** Closes the whole player (RN: dismissing the lock tears down the player). */
  onDismiss?: () => void;
  /** Opens the in-place subscribe picker preselected at `level` (the clip's
   *  required tier). No-op until the creator profile has loaded. */
  openSubscribe: (level: PopStoreExtendedSubscriptionLevel | undefined) => void;
  /**
   * The viewer has PURCHASED the course these clips belong to (one-shot) → every
   * clip is unlocked regardless of tier, like the owner. `false` for series /
   * non-course players and while the check resolves.
   */
  coursePurchased: boolean;
  /**
   * The "Unlock this Course for $X" option the paywall renders beside Subscribe
   * (RN parity) — present only for a standalone-purchasable course the viewer
   * hasn't bought. `null` → subscribe-only paywall.
   */
  courseUnlock: CourseClipUnlock | null;
}

const ClipAccessContext = createContext<ClipAccessState | null>(null);

function useClipAccessContext(): ClipAccessState {
  const ctx = useContext(ClipAccessContext);
  if (!ctx) {
    throw new Error(
      'ClipAccessMonitor / ClipAccessOverlay must be used within <ClipAccessBoundary>',
    );
  }
  return ctx;
}

/**
 * Provides the viewer-standing provider + shared lock state for the clip gate,
 * and mounts the in-place subscribe picker the CTA opens.
 *
 * `onDismiss` closes the HOST player. RN tears down the entire clip player when
 * the lock is dismissed (`AccessBlockedBySubscriptionView.onClosePress` fires
 * `onClose()` AND `goBack()`), so a locked clip is never revealed by closing the
 * paywall. Pass the host's own close handler — `router.back()` on the route
 * players, `onOpenChange(false)` on the dialog player.
 *
 * The subscribe picker mounts IN PLACE (`SubscribeDialog`), NOT by navigating to
 * `/<urlCode>/subscribe` — a route push from a clip page loses the clip context
 * and strands the viewer on the subscribe URL. This mirrors PDP's `BuyNowAction`,
 * which mounts the same dialog locally for exactly that reason. The clip player
 * stays mounted underneath; on follow/subscribe the shared `BioUserAccessProvider`
 * re-resolves and the overlay auto-dismisses — no refresh (RN parity).
 */
export function ClipAccessBoundary({
  sellerId,
  courseListingId,
  courseSlug,
  courseUnlock,
  onDismiss,
  children,
}: {
  sellerId: string;
  /**
   * The derived listing of the COURSE these clips belong to — when the viewer has
   * bought it, every clip unlocks (purchase OR subscription, matching upstream's
   * `CourseVideoAccessControl`). Omit for series / non-course players.
   */
  courseListingId?: string;
  /**
   * The slug of the COURSE these clips belong to. Paired with `courseListingId`,
   * it lets `CoursePurchaseProvider` optimistically flip THIS course to owned the
   * instant its checkout succeeds (matched by the success event's `course_slug`),
   * so a just-bought clip unlocks with no reload even before the backend order is
   * searchable. Omit for series / non-purchasable-course players.
   */
  courseSlug?: string;
  /**
   * The standalone-purchase option for a locked course clip's paywall ("Unlock
   * this Course for $X"). Omit for series / non-purchasable courses. Ignored
   * once the viewer owns the course (the gate bypasses entirely then).
   */
  courseUnlock?: CourseClipUnlock;
  onDismiss?: () => void;
  children: ReactNode;
}) {
  // The provider must wrap the inner state so `ClipAccessInner` can call
  // `useBioUserAccess` (to refresh follow standing after an inline follow).
  // `CoursePurchaseProvider` resolves course-purchase ownership (keyed by
  // `courseListingId`) and, given the course scope (`courseListingId` +
  // `courseSlug`), optimistically flips it to owned on a matching `course`
  // checkout success — so a just-bought course unlocks the player in the same
  // session, with no reload and no race on the backend order index (RN parity).
  return (
    <BioUserAccessProvider creatorId={sellerId}>
      <CoursePurchaseProvider
        courseListingId={courseListingId}
        courseSlug={courseSlug}
      >
        <ClipAccessInner
          sellerId={sellerId}
          courseListingId={courseListingId}
          courseUnlock={courseUnlock}
          onDismiss={onDismiss}
        >
          {children}
        </ClipAccessInner>
      </CoursePurchaseProvider>
    </BioUserAccessProvider>
  );
}

/**
 * Inner boundary — runs INSIDE `BioUserAccessProvider`, so it can refresh the
 * viewer's follow standing after the inline free-tier follow. Holds the lock +
 * subscribe-picker state and mounts `SubscribeDialog`.
 */
function ClipAccessInner({
  sellerId,
  courseListingId,
  courseUnlock,
  onDismiss,
  children,
}: {
  sellerId: string;
  courseListingId?: string;
  courseUnlock?: CourseClipUnlock;
  onDismiss?: () => void;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const { currentUserId, updateFollowingStatus, refreshFollowingStatus } =
    useBioUserAccess();
  // A throwaway GUEST account (minted by a prior course-purchase checkout —
  // "Unlock this Course for $X" — which resolves an actor itself) has a uid, so
  // `currentUserId` is non-null even though the viewer has no real identity.
  // Subscribing/following needs a REAL user, so treat a guest like anonymous and
  // route through login first. Without this, tapping Subscribe after closing the
  // Unlock checkout flashed the picker open, then the subscribe flow re-detected
  // the guest and bounced to login on top of it.
  const isGuest = useIsGuestUser();
  // Course purchase unlocks every clip (like the owner) — resolved by
  // `courseListingId` from the enclosing `CoursePurchaseProvider` and shared
  // with the monitor/overlay via context.
  const { purchased: coursePurchased } = useCoursePurchased(courseListingId);
  const [lockedVideo, setLockedVideo] = useState<AnyVideo | null>(null);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [defaultLevel, setDefaultLevel] =
    useState<PopStoreExtendedSubscriptionLevel>();
  const profile = useCreatorProfile(sellerId);

  // A paid-tier subscribe completes via the soft checkout; `BioUserAccessProvider`
  // itself re-resolves the viewer's standing on the checkout-success event, so the
  // overlay's auto-dismiss effect lifts the gate on return once
  // `extendedSubscriptionLevel` rises — no refresh wiring needed here.

  const openSubscribe = usePersistentOpenSubscribe({
    profileReady: profile != null,
    userId: currentUserId,
    isGuest,
    setDefaultLevel,
    setSubscribeOpen,
  });

  // Inline FREE-tier follow succeeded (doesn't navigate). Close the picker and
  // refresh the provider's follow standing: optimistic flip + a server re-check,
  // so `extendedSubscriptionLevel` rises to FOLLOWER and the overlay's
  // auto-dismiss effect lifts the gate — no reload (RN parity). The paid-tier
  // path navigates to checkout instead and re-resolves on return, so it doesn't
  // route through here.
  const handleFollowed = () => {
    setSubscribeOpen(false);
    updateFollowingStatus(true);
    refreshFollowingStatus();
  };

  return (
    <ClipAccessContext.Provider
      value={{
        sellerId,
        lockedVideo,
        setLockedVideo,
        onDismiss,
        openSubscribe,
        coursePurchased,
        // Offer the standalone buy only while unpurchased (a purchaser's clips
        // aren't locked at all, but guard anyway).
        courseUnlock: coursePurchased ? null : (courseUnlock ?? null),
      }}
    >
      {children}
      {profile && (
        <SubscribeDialog
          open={subscribeOpen}
          onOpenChange={setSubscribeOpen}
          profile={profileSlimmed(profile)}
          defaultLevel={defaultLevel}
          onFollowed={handleFollowed}
          // Paid-tier checkout stays a SOFT nav: `SubscribeDialog` closes first,
          // then pushes `/checkout`, which the root `@checkout/(.)checkout`
          // intercept overlays over the still-mounted clip player (same as PDP's
          // `BuyNowAction`). In the overlay, "Done" returns here via `router.back()`
          // (the clip is the opener); the now-held tier lifts the gate. `successRedirectUrl`
          // = this clip's path is the page-flow fallback (a direct-hit success hard-navs back).
          softCheckoutNav
          successRedirectUrl={pathname}
        />
      )}
    </ClipAccessContext.Provider>
  );
}

/**
 * Builds the `openSubscribe` action. A REAL signed-in viewer opens the picker
 * directly. A CONFIRMED-anonymous viewer — OR a throwaway GUEST (a checkout-minted
 * account with a uid but no real identity) — needs a real identity first
 * (subscribing / following both require a real user): register "reopen the picker"
 * as the post-login callback, then soft-`push` to `/login?next=<here>` so the bio's
 * `@modal/(.)login` intercept overlays it and, on success, `router.back()`s here
 * and fires the callback. 1:1 with `BuyNowAction.goToSubscribe` and `FollowGuardModal`.
 */
function usePersistentOpenSubscribe({
  profileReady,
  userId,
  isGuest,
  setDefaultLevel,
  setSubscribeOpen,
}: {
  profileReady: boolean;
  userId: string | null | undefined;
  /**
   * Tri-state guest status ({@link useIsGuestUser}): `undefined` while resolving,
   * `true` for a throwaway guest account (a uid but no real identity), `false`
   * for a real user or nobody. A guest must log in for real before subscribing.
   */
  isGuest: boolean | undefined;
  setDefaultLevel: (
    level: PopStoreExtendedSubscriptionLevel | undefined,
  ) => void;
  setSubscribeOpen: (open: boolean) => void;
}) {
  // `login` / `router` are read here rather than threaded in as params — this is
  // a hook, so calling `useLogin()` / `useRouter()` unconditionally at the top
  // obeys the Rules of Hooks and keeps the caller from wiring them through.
  const login = useLogin();
  const router = useRouter();

  return (level: PopStoreExtendedSubscriptionLevel | undefined) => {
    if (!profileReady) return;
    setDefaultLevel(level);
    // Auth loads lazily — `userId` is `undefined` until it resolves. Ignore a tap
    // made before then, else a signed-in viewer would be misread as logged-out
    // and bounced to /login.
    if (userId === undefined) return;
    // A confirmed-anonymous viewer (`null`) OR a throwaway GUEST (`isGuest`) has
    // no real identity — subscribing/following needs one, so route through login
    // and reopen the picker on success. A guest's `isGuest` may still be `in
    // flight` (`undefined`) for a present uid; ignore the tap until it resolves
    // so we never open the picker for a guest and then bounce to login on top.
    if (userId !== null && isGuest === undefined) return;
    if (userId === null || isGuest) {
      // Restricted clip/course unlock → open the login modal on "Community
      // Member" (fan-facing), not the default "Creator or Business".
      login({
        onSuccess: () => {
          // The callback OWNS the post-login nav: the login modal is left on its
          // "Signing you in…" overlay, so pop the intercepted `/login` FIRST,
          // then open the picker over the clip. Without the `router.back()` the
          // login modal stayed frozen behind the picker — closing the picker
          // revealed it still spinning (the reported bug).
          router.back();
          setSubscribeOpen(true);
        },
      });
      return;
    }
    setSubscribeOpen(true);
  };
}

// ─── Monitor: arms the preview timer, flips the shared lock state ───────────

/**
 * Watches the active clip and locks it when the viewer's standing doesn't meet
 * its `accessLevel`. Mirrors RN: unrestricted / permitted clips play; a locked
 * clip plays `previewDuration` seconds (0 → locked immediately) then flips the
 * overlay on and mutes if `muteForUnsubscribedUsers`. The creator (owner) always
 * bypasses. Renders nothing — it only drives the shared lock state.
 */
export function ClipAccessMonitor({ videos }: { videos: AnyVideo[] }) {
  const { sellerId, setLockedVideo, coursePurchased } = useClipAccessContext();
  const { currentIndex$ } = useVideoClipsContext();
  const { muted$ } = useAudioMuteContext();
  const [currentIndex] = useBehaviorSubject(currentIndex$);

  const currentUserId = useCurrentUserId();
  const { extendedSubscriptionLevel, loading } = useBioUserAccess();
  // Owner viewing their own clips bypasses the gate (matches AccessGuard's
  // `isOwner` rule — NOT `useMyPopStore().editable`, which is false on the
  // public bio, so the owner would otherwise be gated there).
  const isOwner = currentUserId != null && currentUserId === sellerId;

  const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (lockTimerRef.current != null) {
      clearTimeout(lockTimerRef.current);
      lockTimerRef.current = null;
    }

    // Any index change clears a prior lock; it re-arms below if still locked.
    setLockedVideo(null);

    // Hold off until the viewer's standing has resolved — otherwise a real
    // subscriber reads as a stranger (`undefined`) on first render and a
    // no-preview clip would flash the paywall before standing lands. The effect
    // re-runs when `loading`/`extendedSubscriptionLevel` settle.
    if (loading) return;

    // Owner and course-purchaser bypass the tier gate entirely.
    if (isOwner || coursePurchased) return;

    const video = videos[currentIndex];
    if (!video) return;

    const contentLevel = (video as { accessLevel?: string }).accessLevel;
    if (!isClipLockedForViewer(contentLevel, extendedSubscriptionLevel)) {
      return;
    }

    const rawPreview = Number(
      (video as { previewDuration?: unknown }).previewDuration,
    );
    const previewMs = (Number.isFinite(rawPreview) ? rawPreview : 3) * 1000;
    const shouldMute =
      (video as { muteForUnsubscribedUsers?: boolean })
        .muteForUnsubscribedUsers === true;

    if (previewMs <= 0) {
      setLockedVideo(video);
      if (shouldMute) muted$.next(true);
      return;
    }

    lockTimerRef.current = setTimeout(() => {
      lockTimerRef.current = null;
      setLockedVideo(video);
      if (shouldMute) muted$.next(true);
    }, previewMs);

    return () => {
      if (lockTimerRef.current != null) {
        clearTimeout(lockTimerRef.current);
        lockTimerRef.current = null;
      }
    };
  }, [
    currentIndex,
    videos,
    isOwner,
    coursePurchased,
    loading,
    extendedSubscriptionLevel,
    muted$,
    setLockedVideo,
  ]);

  return null;
}

// ─── Overlay: the blurred paywall card ──────────────────────────────────────

const ACCESS_LEVEL_LABELS: Record<string, string> = {
  [PopStoreExtendedUserAccessLevel.VIP]: 'VIP',
  [PopStoreExtendedUserAccessLevel.FAN]: 'FAN',
  [PopStoreExtendedUserAccessLevel.SUBSCRIBER]: 'SUBSCRIBER',
  [PopStoreExtendedUserAccessLevel.FOLLOWER]: 'FOLLOWER',
};

/**
 * Map a clip's raw content `accessLevel` to the subscribe picker's `defaultLevel`
 * (`PopStoreExtendedSubscriptionLevel`). The required-access and subscription
 * extended enums share identical string values, so a `follower` clip preselects
 * the free FOLLOWER tier — the picker turns that into a follow rather than a paid
 * subscription. `undefined` (e.g. `all`) → no preselection. Reuses
 * {@link clipRequiredAccessLevel} (same widening, incl. `follower`).
 */
function contentLevelToSubscribeLevel(
  accessLevel: string | undefined,
): PopStoreExtendedSubscriptionLevel | undefined {
  return clipRequiredAccessLevel(accessLevel) as unknown as
    | PopStoreExtendedSubscriptionLevel
    | undefined;
}

/**
 * The paywall shown over a locked clip. Renders only when a clip is locked.
 *
 * Both the follower and the paid-tier CTA open the IN-PLACE subscribe picker
 * (`ClipAccessBoundary`'s `SubscribeDialog`) preselected at the required level —
 * `follower` for a follower-only clip. This mirrors RN: the CTA does NOT call the
 * follow API inline; RN's `AccessBlockedBySubscriptionView` →
 * `useSubscribeToCreator` opens the subscribe screen at `defaultLevel` (FOLLOWER
 * for follower content), and the follow/subscribe happens INSIDE that flow. The
 * boundary's `openSubscribe` also owns the anonymous-login gate (RN's
 * `withAuthenticated`), so there is no separate login branch here.
 *
 * When the subscribe/follow completes, the picker closes and the shared
 * `BioUserAccessProvider` re-resolves the viewer's standing; the auto-dismiss
 * effect then clears the lock and the clip plays — no refresh, matching RN.
 */
export function ClipAccessOverlay() {
  const {
    sellerId,
    lockedVideo,
    setLockedVideo,
    onDismiss,
    openSubscribe,
    coursePurchased,
    courseUnlock,
  } = useClipAccessContext();
  const { extendedSubscriptionLevel } = useBioUserAccess();
  // The creator's configured tiers — used to show the viewer's CURRENT level by
  // its CUSTOM name (e.g. "MyFan edit ren"), matching the subscribe modal, not
  // the generic "Subscriber" label. 1:1 with RN's `getAccessLevelDetails.getText`
  // (`getSubscriptionDisplayName`), which falls back to the generic tier label
  // until the subscriptions load.
  const { subscriptions } = useFetchAllSubscriptions(sellerId);

  const contentLevel = (lockedVideo as { accessLevel?: string } | null)
    ?.accessLevel;

  // Unlock the overlay the moment the viewer gains access — a follow/subscription
  // resolves, or the course-purchase check lands (it may resolve after a preview
  // already armed the lock) — while the card is open.
  useEffect(() => {
    if (!lockedVideo) return;
    if (
      coursePurchased ||
      !isClipLockedForViewer(contentLevel, extendedSubscriptionLevel)
    ) {
      setLockedVideo(null);
    }
  }, [
    lockedVideo,
    contentLevel,
    coursePurchased,
    extendedSubscriptionLevel,
    setLockedVideo,
  ]);

  if (!lockedVideo) return null;

  // `follower` is the FREE tier — the CTA copy differs, but both tiers route to
  // the same subscribe picker (the picker turns a `follower` default into the
  // free follow).
  const isFollower = contentLevel === PopStoreExtendedUserAccessLevel.FOLLOWER;
  // The clip's REQUIRED tier as a subscription level, for the header tier icon.
  // `undefined` for `all` / `follower` (no paid badge) — matching RN's guard.
  const requiredLevel = contentLevelToSubscribeLevel(contentLevel);

  // Resolve a tier's display name: prefer the creator's CUSTOM name (uppercased)
  // — the same value the subscribe modal shows — and fall back to the generic
  // tier label until the creator's subscriptions load. 1:1 with RN's `getText`.
  // Used for BOTH the required-tier header AND the viewer's current level, so the
  // two read consistently (e.g. both "MYFAN EDIT REN", not one custom + one
  // generic "VIP").
  const levelDisplayName = (level: string | undefined): string => {
    if (!level) return 'SUBSCRIBER';
    const generic = ACCESS_LEVEL_LABELS[level] ?? level.toUpperCase();
    if (!subscriptions) return generic;
    // `platform_sub_level` (paid subset) shares its string values with the
    // extended enum; compare as strings to bridge the two enum types.
    const sub = subscriptionsToList(subscriptions).find(
      s => (s.platform_sub_level as string) === level,
    );
    return sub ? getSubscriptionDisplayName(sub).toUpperCase() : generic;
  };

  const label = levelDisplayName(contentLevel);

  // The viewer's CURRENT subscription level, shown so they can see their tier
  // vs. the required one (QA). Mirror RN's gate 1:1: `isUpgradeRequired &&
  // !isFollowerLevel`, where
  //   isUpgradeRequired = userSubscriptionLevel != null   (viewer HAS a level)
  //                       && desiredAccessLevel !== ALL    (clip is gated)
  //                       && !permitted                    (viewer locked out)
  //   isFollowerLevel   = the REQUIRED clip tier is FOLLOWER (not the viewer's).
  // `isFollowerLevel` keys off the required tier, so a FOLLOWER *viewer* hitting
  // a paid clip still sees their Follower badge (the reported bug excluded them).
  // Compute the predicate in full rather than leaning on "the overlay only mounts
  // when locked" — so the card stays correct even if this ever renders unlocked.
  const isUpgradeRequired =
    extendedSubscriptionLevel != null &&
    isAccessGated(contentLevel) &&
    isClipLockedForViewer(contentLevel, extendedSubscriptionLevel);
  const showCurrentLevel = isUpgradeRequired && !isFollower;
  const currentLevelName = levelDisplayName(extendedSubscriptionLevel);

  const handleCta = () => {
    // Both tiers → the in-place subscribe picker with the required level
    // preselected (`follower` for a follower clip). `openSubscribe` owns the
    // anonymous-login gate + the actual follow/subscribe; on completion the
    // provider re-resolves and the auto-dismiss effect unlocks the clip. 1:1
    // with RN's `useSubscribeToCreator`.
    openSubscribe(contentLevelToSubscribeLevel(contentLevel));
  };

  // Closing the paywall closes the WHOLE player (RN parity) — a locked clip is
  // never revealed by dismissing the lock. Falls back to just clearing the lock
  // if the host gave no close handler (shouldn't happen for the clip players).
  const handleClose = () => {
    setLockedVideo(null);
    onDismiss?.();
  };

  return (
    // `z-[60]` sits above the player's own comment sheet (`z-40`/`z-50`) but
    // BELOW app-level dialogs, which portal to `<body>` at `z-[90]` (the login
    // / subscribe modals the CTA opens). The old `z-[9999]` (the "above
    // everything" echo-bot value) rendered this overlay ON TOP of the opened
    // login modal, covering half of it — the clipped-modal bug.
    <div
      className="bg-scrim/40 absolute inset-0 z-[60] flex items-center justify-center backdrop-blur-[25px]"
      onClick={handleClose}
    >
      <div
        className="bg-surface-1 relative mx-6 w-full max-w-sm rounded-2xl p-4"
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-2.5 top-2.5 z-10 flex h-6 w-6 items-center justify-center"
          aria-label="Close"
        >
          <X className="text-content-1 h-5 w-5" />
        </button>

        <div className="flex items-center justify-center gap-1.5 pt-2">
          <span className="text-content-1 text-center text-sm font-bold">
            This is a
          </span>
          {/* Required-tier ICON — the per-tier `SubscriptionLevelBadge`, matching
              RN's `accessLevelDetails.icon` (not a generic lock). FOLLOWER has no
              badge, so a follower clip shows no icon (RN's `icon != null` guard). */}
          {requiredLevel && (
            <SubscriptionLevelBadge level={requiredLevel} iconOnly />
          )}
          <span className="text-content-1 text-center text-sm font-bold">
            {label} level clip
          </span>
        </div>

        <p className="text-content-1 mt-2.5 text-center text-sm">
          {isFollower
            ? 'Only followers or subscribed users '
            : `Only users with ${label} subscription `}
          can view this clip and much more exclusive content from this creator.
        </p>

        {/* Current subscription level — so the viewer sees their tier alongside
            the required one (QA). RN's "Your Subscription level" card, shown only
            for a paid subscriber below the required tier: the tier ICON (shared
            `SubscriptionLevelBadge iconOnly`) + the creator's CUSTOM tier name,
            matching what the subscribe modal shows (RN `getSubscriptionDisplayName`). */}
        {showCurrentLevel && extendedSubscriptionLevel && (
          <div className="bg-line mx-5 mt-2.5 flex flex-col items-center gap-1 rounded-2xl px-4 py-2.5">
            <span className="text-content-1 text-center text-sm">
              Your Subscription level
            </span>
            <span className="flex items-center gap-1.5">
              <SubscriptionLevelBadge
                level={extendedSubscriptionLevel}
                iconOnly
              />
              <span className="text-content-1 text-sm font-semibold">
                {currentLevelName}
              </span>
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={handleCta}
          className="bg-brand text-content-on-brand mt-3 w-full rounded-full py-2.5 text-center text-sm font-semibold transition-opacity hover:opacity-80"
        >
          {isFollower ? 'Follow to view!' : 'Subscribe to view!'}
        </button>

        {/* Standalone course purchase — RN's "Unlock this Course for $X"
            outlined button beside Subscribe (only for a purchasable, unbought
            course). Opens the course checkout; on success the provider
            re-resolves and the auto-dismiss effect unlocks the clip. */}
        {courseUnlock && (
          <button
            type="button"
            onClick={courseUnlock.onPurchase}
            className="border-brand text-brand mt-3 flex w-full items-center justify-center gap-1.5 rounded-full border py-2.5 text-center text-sm font-semibold transition-opacity hover:opacity-80"
          >
            <span>Unlock this Course for</span>
            <span className="font-bold">{courseUnlock.priceLabel}</span>
            {courseUnlock.originalPriceLabel && (
              <span className="text-raise/40 font-bold line-through">
                {courseUnlock.originalPriceLabel}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
